import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useProState } from "../lib/pro";
import UpgradeButton from "../components/UpgradeButton";
import { toast } from "sonner";

const LOGO_SRC = "/popupprofit-logo.png";

export default function Login() {
  const { login, refresh, saveEmail } = useProState();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [restoreMode, setRestoreMode] = useState(false);
  const [restoreEmail, setRestoreEmail] = useState("");
  const [restoreLoading, setRestoreLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) { setError("Please enter a valid email"); return; }
    if (!password) { setError("Please enter your password"); return; }
    setLoading(true);
    try {
      await login(trimmed, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Could not sign you in");
    } finally {
      setLoading(false);
    }
  };

  const onRestore = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = restoreEmail.trim().toLowerCase();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) { setError("Please enter a valid email"); return; }
    setRestoreLoading(true);
    try {
      const data = await refresh(trimmed);
      if (data?.pro) {
        saveEmail(trimmed);
        toast.success("Pro access restored");
        navigate("/dashboard");
      } else {
        setError("No active Pro subscription found for that email.");
      }
    } catch {
      setError("Could not check Pro status. Please try again.");
    } finally {
      setRestoreLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24" data-testid="login-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-mute hover:text-brand-green mb-6" data-testid="login-back">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>

      <div className="pop-card p-7 sm:p-8 text-center">
        <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-white border border-brand-line overflow-hidden">
          <img src={LOGO_SRC} alt="PopUp Profit logo" className="w-12 h-12 object-contain" />
        </span>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-brand-ink mt-5">
          {restoreMode ? "Restore Pro access" : "Welcome back"}
        </h1>
        <p className="text-sm text-brand-mute mt-2">
          {restoreMode
            ? "Subscribed to Pro before creating an account? Enter that email to restore access on this device."
            : "Sign in to your PopUp Profit account."}
        </p>

        {!restoreMode ? (
          <form onSubmit={onSubmit} className="mt-6 text-left" data-testid="login-form" noValidate>
            <div>
              <label className="pop-label" htmlFor="login-email">Email</label>
              <div className="relative mt-1">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute" />
                <input
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@vendor.shop"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pop-input pl-10"
                  data-testid="login-email-input"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="pop-label" htmlFor="login-password">Password</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute" />
                <input
                  id="login-password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pop-input pl-10 pr-10"
                  data-testid="login-password-input"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-mute hover:text-brand-ink"
                  tabIndex={-1}
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="mt-3 text-sm text-brand-error" data-testid="login-error">{error}</p>
            )}

            <button
              type="submit"
              className="pop-btn-primary w-full mt-5 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="login-submit"
              disabled={loading}
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
              ) : (
                <>Sign In</>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={onRestore} className="mt-6 text-left" data-testid="restore-form" noValidate>
            <label className="pop-label" htmlFor="restore-email">Pro subscriber email</label>
            <div className="relative mt-1">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute" />
              <input
                id="restore-email"
                type="email"
                inputMode="email"
                placeholder="you@vendor.shop"
                value={restoreEmail}
                onChange={(e) => setRestoreEmail(e.target.value)}
                className="pop-input pl-10"
                data-testid="restore-email-input"
                disabled={restoreLoading}
              />
            </div>
            {error && (
              <p className="mt-3 text-sm text-brand-error" data-testid="restore-error">{error}</p>
            )}
            <button
              type="submit"
              className="pop-btn-primary w-full mt-5 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="restore-submit"
              disabled={restoreLoading}
            >
              {restoreLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</>
              ) : (
                <>Restore Pro access</>
              )}
            </button>
            {error && error.toLowerCase().includes("no active") && (
              <div className="mt-3 text-center">
                <UpgradeButton variant="primary" label="Subscribe — $9/month" testid="restore-upgrade-btn" />
              </div>
            )}
          </form>
        )}

        <button
          type="button"
          onClick={() => { setRestoreMode((m) => !m); setError(""); }}
          className="mt-5 text-xs font-medium text-brand-mute hover:text-brand-ink"
          data-testid="login-toggle-restore"
        >
          {restoreMode ? "← Back to sign in" : "Have Pro but no account? Restore by email →"}
        </button>

        <p className="text-xs text-brand-mute mt-6">
          New to PopUp Profit?{" "}
          <Link to="/signup" className="text-brand-green font-medium hover:underline" data-testid="login-to-signup">
            Sign up free →
          </Link>
        </p>
      </div>
    </div>
  );
}
